import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { StoreErrorHandler } from '@zero/shared/util-global-state';
import { catchError, filter, map, of, switchMap, tap } from 'rxjs';
import { InstrumentDetailInfrastructure } from '../infrastructure/instrument-detail.infrastructure';
import { instrumentDetailActions, instrumentDetailApiActions } from './instrument-detail.actions';
import { instrumentDetailFeature } from './instrument-detail.reducer';

@Injectable({ providedIn: 'root' })
export class InstrumentDetailEffects {
  #store = inject(Store);
  #actions$ = inject(Actions);
  #instrumentDetailInfrastructure = inject(InstrumentDetailInfrastructure);
  #storeErrorHandler = inject(StoreErrorHandler);

  public loadAssetTimeline$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(instrumentDetailActions.loadAssetTimeline),
      concatLatestFrom(() => this.#store.select(instrumentDetailFeature.selectAssetTimelineInitiallyLoaded)),
      filter(([{ force }, loaded]) => !loaded || force),
      map(([{ params }]) => instrumentDetailApiActions.loadAssetTimelineFromApi({ params })),
    );
  });

  public loadAssetTimelineFromApi$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(instrumentDetailApiActions.loadAssetTimelineFromApi),
      switchMap(({ params }) =>
        this.#instrumentDetailInfrastructure.loadAssetTimeline(params).pipe(
          map((assetTimeline) => instrumentDetailApiActions.loadAssetTimelineSuccess({ isin: params.isin, assetTimeline })),
          catchError((error) => of(instrumentDetailApiActions.loadAssetTimelineFailure({ error }))),
        ),
      ),
    );
  });

  public loadAssetTimelineInfo$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(instrumentDetailActions.loadAssetTimelineInfo),
      switchMap(({ isin }) =>
        this.#instrumentDetailInfrastructure.loadAssetTimelineInfo(isin).pipe(
          map((assetTimelineInfo) => instrumentDetailApiActions.loadAssetTimelineInfoSuccess({ assetTimelineInfo })),
          catchError((error) => of(instrumentDetailApiActions.loadAssetTimelineInfoFailure({ error }))),
        ),
      ),
    );
  });

  public loadDerivative$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(instrumentDetailActions.loadDerivativeData),
      switchMap(({ isin }) =>
        this.#instrumentDetailInfrastructure.loadDerivativeData(isin).pipe(
          map((derivative) => instrumentDetailApiActions.loadDerivativeDataSuccess({ derivative })),
          catchError((error) => of(instrumentDetailApiActions.loadDerivativeDataFailure({ error }))),
        ),
      ),
    );
  });

  public checkDerivativeDataVisibility$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(instrumentDetailActions.checkDerivativeDataVisibility),
      switchMap(() =>
        this.#instrumentDetailInfrastructure
          .checkDerivativeDataVisibility()
          .pipe(map((visibility) => instrumentDetailActions.setDerivativeDataVisibility({ visibility }))),
      ),
    );
  });

  public toggleDerivativeBaseDataVisibility$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(instrumentDetailActions.toggleDerivativeBaseDataVisibility),
      switchMap(() =>
        this.#instrumentDetailInfrastructure
          .toggleDerivativeBaseDataVisibility()
          .pipe(map((visibility) => instrumentDetailActions.setDerivativeDataVisibility({ visibility }))),
      ),
    );
  });

  public toggleDerivativeKeyDataVisibility$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(instrumentDetailActions.toggleDerivativeKeyDataVisibility),
      switchMap(() =>
        this.#instrumentDetailInfrastructure
          .toggleDerivativeKeyDataVisibility()
          .pipe(map((visibility) => instrumentDetailActions.setDerivativeDataVisibility({ visibility }))),
      ),
    );
  });

  public toggleDerivativePayoutProfileVisibility$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(instrumentDetailActions.toggleDerivativePayoutProfileVisibility),
      switchMap(() =>
        this.#instrumentDetailInfrastructure
          .toggleDerivativePayoutProfileVisibility()
          .pipe(map((visibility) => instrumentDetailActions.setDerivativeDataVisibility({ visibility }))),
      ),
    );
  });

  public cacheDerivativeDataVisibility$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(instrumentDetailActions.setDerivativeDataVisibility),
        tap(({ visibility }) => this.#instrumentDetailInfrastructure.cacheDerivativeDataVisibility(visibility)),
      );
    },
    { dispatch: false },
  );

  public handleBackendError$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(
          instrumentDetailApiActions.loadAssetTimelineFailure,
          instrumentDetailApiActions.loadAssetTimelineInfoFailure,
          instrumentDetailApiActions.loadDerivativeDataFailure,
        ),
        tap(({ error }) => this.#storeErrorHandler.handleBackendError(error)),
      );
    },
    { dispatch: false },
  );
}
